// S11 · the TA-owned interview framework by level under Settings (CR-019: "TA-owned interview framework loads by role
// level"; CR-066: "TA Admin configuration without code … Change→Validate→Version→Activate→Audit; historical records retain
// the configuration version used"; CR-022: the level's assessment default, a Suggested value on the plan). Route
// `#settings/framework/<level>`, registered in views/b4/register.ts (C-B4-7).
//
// Master–detail (DC-122): the three level cards (Manager & Below · Director · VP+) beside the chosen level's current
// version — its rounds named from kind and sequence (Recruiter screen · HM interview · Final Interview Loop · Round n,
// never the stored label), each with its format, its suggested ROOTS values and executive participation; the assessment
// default (Required · Recommended · Optional); and the level's required skills, which live in `levels` (one authority)
// and are proposed there. **Submit** proposes a new version through `config.propose` (two-person publication: a
// different Admin approves; the level's `requires_all_roots` stays locked). Durations are data, never drawn here.
//
// Seats: the Admin edits (his TA Admin and HR Systems Admin fold into it, DC-228); a reader of configuration sees the
// values; every other seat has no page.

import { useEffect, useId, useMemo, useState } from "react";
import {
  ACTION_LABELS, COMMON_LABELS, G1_LABELS, G6_LABELS, JOURNEY_LABELS, LEVEL_DISPLAY, REQUEST_STATUS, ROOTS_LABELS, ROOTS_ORDER, SCORECARD_LABELS,
  SURFACE_LABELS, Button, Card, Field, Flex, Input, LevelGroup, McRoot, Pill, Recorded, Row, SectionTitle, Segmented, Split, Stack, TextArea,
  Toggle, ValueChips, VersionTags, type ErrorStripProps, type Hs, type Level, type RootsValue, type SegmentedOption,
} from "@/mc";
import { configList, configOpen, configPropose } from "@/api/tools";
import { businessLocalToISO, isoToBusinessLocal } from "@/api/runtime";
import { useRead, useWrite } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { ASSESSMENT_WORDS, FRAMEWORK_WORDS } from "./assessment/labels";
import {
  LEVELS, failureOf, frameworkAccess, frameworkRoundKey, isLevel, roundName, type AssessmentDefault, type FrameworkAccess, type FrameworkRoundLike,
  type FrameworkShapeLike, type LevelLimitsLike,
} from "./assessment/shapes";

/** A framework route's level is one of the three domain levels, or absent (the first level). */
export function frameworkLevelOk(level: string | undefined): boolean {
  return level === undefined || level === "" || isLevel(level);
}

export interface FrameworkVersionTag {
  n: number;
  current: boolean;
  draft: boolean;
}

export interface FrameworkProposal {
  /** The changed framework body, or null when only the required skills changed. */
  framework: FrameworkShapeLike | null;
  /** The level's new minimum of required skills, or null when unchanged. */
  minSkills: number | null;
  /** `datetime-local` in the business zone. */
  effectiveFrom: string;
  note: string;
}

export interface FrameworkConfigViewProps {
  level: Level;
  access: FrameworkAccess;
  /** The current version's body (`interview_framework:<level>`). */
  framework: FrameworkShapeLike | null;
  versions?: readonly FrameworkVersionTag[] | undefined;
  limits?: LevelLimitsLike | null | undefined;
  loading?: boolean | undefined;
  busy?: boolean | undefined;
  refused?: string | undefined;
  failure?: ErrorStripProps | undefined;
  saved?: boolean | undefined;
  /** The proposal's default effective instant (`datetime-local`); the host passes one business day or more ahead. */
  defaultEffective?: string | undefined;
  onLevel?: ((level: Level) => void) | undefined;
  onSubmit?: ((proposal: FrameworkProposal) => void) | undefined;
}

const FORMATS = ["virtual", "onsite"] as const;
const FORMAT_WORD: Readonly<Record<string, string>> = { virtual: G1_LABELS.virtual, onsite: G1_LABELS.onsite };
const DEFAULTS: readonly AssessmentDefault[] = ["required", "recommended", "optional"];
const DEFAULT_WORD: Readonly<Record<AssessmentDefault, string>> = {
  required: COMMON_LABELS.required,
  recommended: FRAMEWORK_WORDS.recommended,
  optional: COMMON_LABELS.optional,
};

/** The values' hotspots of one round: `framework.round.<key>.value.<value>`. */
function valueHs(key: string): Partial<Record<RootsValue, Hs>> {
  return Object.fromEntries(ROOTS_ORDER.map((value) => [value, `framework.round.${key}.value.${value}`]));
}

/** The framework page itself: no data access, so a seat's rendering is a function of what it was given. */
export function FrameworkConfigView({
  level, access, framework, versions = [], limits, loading, busy, refused, failure, saved, defaultEffective = "", onLevel, onSubmit,
}: FrameworkConfigViewProps) {
  const id = useId();
  const editable = access === "edit";
  const mode = editable ? "edit" : "view";
  const [draft, setDraft] = useState<FrameworkShapeLike | null>(framework);
  const [minSkills, setMinSkills] = useState<number | null>(limits?.min_skills ?? null);
  const [effective, setEffective] = useState(defaultEffective);
  const [note, setNote] = useState("");
  const [tried, setTried] = useState(false);
  useEffect(() => setDraft(framework), [framework]);
  useEffect(() => setMinSkills(limits?.min_skills ?? null), [limits?.min_skills]);
  useEffect(() => setEffective(defaultEffective), [defaultEffective]);
  const changedFramework = useMemo(() => JSON.stringify(draft) !== JSON.stringify(framework), [draft, framework]);
  const changedSkills = minSkills !== null && limits !== null && limits !== undefined && minSkills !== limits.min_skills;

  if (access === "none") return null;

  const setRound = (index: number, round: FrameworkRoundLike) => {
    if (!draft) return;
    setDraft({ ...draft, rounds: draft.rounds.map((old, at) => (at === index ? round : old)) });
  };
  const current = versions.find((version) => version.current)?.n ?? 0;
  const pending = versions.some((version) => version.draft);
  const defaultOptions: SegmentedOption[] = DEFAULTS.map((value) => ({
    value,
    label: DEFAULT_WORD[value],
    hs: value === "required" ? "framework.assessment_default" : `framework.assessment_default.${value}`,
  }));
  const defaultTitle = `${id}-default`;
  const noteShort = note.trim().length < 3;

  const main = (
    <Card
      title={LEVEL_DISPLAY[level]}
      pills={pending ? <Pill>{REQUEST_STATUS.draft}</Pill> : undefined}
      loading={loading ? { lines: 6 } : undefined}
      refused={refused ? { reason: refused } : undefined}
      error={failure}
    >
      <Stack gap={16}>
        {versions.length ? <VersionTags versions={versions.map((version) => ({ n: version.n }))} current={current} label={JOURNEY_LABELS.version} /> : null}
        {draft ? (
          <div role="list">
            <Row head lead="24%" cols={4}>
              <span />
              <span />
              {ROOTS_LABELS.roots}
              {SURFACE_LABELS.executiveParticipation}
            </Row>
            {draft.rounds.map((round, index) => {
              const key = frameworkRoundKey(round.kind, round.sequence);
              const nameId = `${id}-${key}`;
              const formats: SegmentedOption[] = FORMATS.map((value) => ({
                value,
                label: FORMAT_WORD[value] ?? value,
                hs: value === "onsite" ? `framework.round.${key}.format` : `framework.round.${key}.format.${value}`,
              }));
              return (
                <div role="listitem" key={key}>
                  <Row lead="24%" cols={4} off={!round.in_use}>
                    <span id={nameId}>{roundName(round.kind, round.sequence)}</span>
                    <Segmented
                      options={formats}
                      value={round.format}
                      aria-labelledby={nameId}
                      mode={mode}
                      onChange={(value) => setRound(index, { ...round, format: value })}
                    />
                    <ValueChips
                      value={round.suggested_values.filter((value): value is RootsValue => (ROOTS_ORDER as readonly string[]).includes(value))}
                      fixed={round.kind === "hm_round"}
                      aria-labelledby={nameId}
                      mode={mode}
                      hs={valueHs(key)}
                      onChange={(values) => setRound(index, { ...round, suggested_values: ROOTS_ORDER.filter((value) => values.includes(value)) })}
                    />
                    <Toggle
                      label={SURFACE_LABELS.executiveParticipation}
                      hideLabel
                      checked={round.executive_participation}
                      mode={mode}
                      
                      onChange={(checked) => setRound(index, { ...round, executive_participation: checked })}
                    />
                  </Row>
                </div>
              );
            })}
          </div>
        ) : null}
        {draft ? (
          <div>
            <SectionTitle id={defaultTitle}>{ASSESSMENT_WORDS.assessment}</SectionTitle>
            <Segmented
              options={defaultOptions}
              value={draft.assessment_default}
              aria-labelledby={defaultTitle}
              mode={mode}
              onChange={(value) => setDraft({ ...draft, assessment_default: value as AssessmentDefault })}
            />
          </div>
        ) : null}
        {limits ? (
          <Field label={FRAMEWORK_WORDS.requiredSkills} mode={mode}>
            <Input
              type="number"
              min={1}
              max={limits.max_skills}
              value={String(minSkills ?? limits.min_skills)}
              
              onChange={(event) => {
                const n = Math.round(Number(event.target.value));
                if (Number.isFinite(n)) setMinSkills(Math.min(limits.max_skills, Math.max(1, n)));
              }}
            />
          </Field>
        ) : null}
        {editable ? (
          <>
            <Field label={G6_LABELS.effectiveVersion} required>
              <Input type="datetime-local" value={effective}  onChange={(event) => setEffective(event.target.value)} />
            </Field>
            <Field label={SURFACE_LABELS.notes} required error={tried && noteShort ? COMMON_LABELS.required : undefined}>
              <TextArea value={note} maxLength={2000} rows={3}  onChange={(event) => setNote(event.target.value)} />
            </Field>
            <Flex>
              <Button
                tone="primary"
                busy={busy}
                
                onClick={() => {
                  setTried(true);
                  if (noteShort || !effective || (!changedFramework && !changedSkills)) return;
                  onSubmit?.({
                    framework: changedFramework ? draft : null,
                    minSkills: changedSkills ? minSkills : null,
                    effectiveFrom: effective,
                    note: note.trim(),
                  });
                }}
              >
                {ACTION_LABELS.submit}
              </Button>
            </Flex>
          </>
        ) : null}
        {saved ? <Recorded live /> : null}
      </Stack>
    </Card>
  );

  const side = (
    <LevelGroup
      label={SCORECARD_LABELS.level}
      value={level}
      levels={LEVELS}
      hs={Object.fromEntries(LEVELS.map((value) => [value, `framework.level.${value}`]))}
      onChange={onLevel}
    />
  );

  return <Split main={main} side={side} />;
}

/** One business day ahead is the earliest a non-hotfix version may take effect; four calendar days clears a weekend. */
function defaultEffectiveFrom(): string {
  return isoToBusinessLocal(new Date(Date.now() + 4 * 86_400_000).toISOString());
}

/**
 * The page with its data path: `config.list` / `config.open` for the level's current framework version and for
 * `levels`, `config.propose` for a new version of either. Choosing another level moves the route.
 */
export function FrameworkConfig({ level }: { level?: string | undefined }) {
  const { me, can } = useSession();
  const access = frameworkAccess(me?.roles ?? [], can);
  const chosen: Level = isLevel(level) ? level : "manager_and_below";
  const key = `interview_framework:${chosen}`;
  const list = useRead(["config", "list", key], () => configList({ kind: "interview_framework", key, limit: 50 }), { enabled: access !== "none" });
  const currentRow = list.data?.rows.find((row) => row.label === "Current") ?? null;
  const body = useRead(["config-open", currentRow?.id ?? ""], () => configOpen({ id: currentRow?.id ?? "" }), { enabled: currentRow !== null });
  const levelsList = useRead(["config", "list", "levels"], () => configList({ key: "levels", status: "effective", limit: 10 }), { enabled: access !== "none" });
  const levelsRow = levelsList.data?.rows.find((row) => row.label === "Current") ?? null;
  const levelsBody = useRead(["config-open", levelsRow?.id ?? ""], () => configOpen({ id: levelsRow?.id ?? "" }), { enabled: levelsRow !== null });
  const propose = useWrite(configPropose, [["config"], ["config-open"]]);
  const [effective] = useState(defaultEffectiveFrom);

  const framework = (body.data?.payload ?? null) as unknown as FrameworkShapeLike | null;
  const levels = (levelsBody.data?.payload ?? null) as unknown as Record<string, Record<string, unknown>> | null;
  const limitsRaw = levels?.[chosen];
  const minRaw = limitsRaw?.["min_skills"];
  const maxRaw = limitsRaw?.["max_skills"];
  const limits: LevelLimitsLike | null = typeof minRaw === "number" && typeof maxRaw === "number" ? { min_skills: minRaw, max_skills: maxRaw } : null;
  const versions: FrameworkVersionTag[] = (list.data?.rows ?? [])
    .filter((row) => row.status !== "rejected")
    .map((row) => ({ n: row.version, current: row.label === "Current", draft: row.status === "draft" }))
    .sort((a, b) => a.n - b.n);
  const error = list.error ?? body.error ?? levelsList.error ?? levelsBody.error ?? propose.error;
  const { refused, failure } = failureOf(error, () => { propose.reset(); void list.refetch(); });

  const submit = async (proposal: FrameworkProposal) => {
    const effective_from = businessLocalToISO(proposal.effectiveFrom);
    if (proposal.framework) {
      await propose.mutateAsync({ kind: "interview_framework", key, payload: { ...proposal.framework }, effective_from, note: proposal.note });
    }
    if (proposal.minSkills !== null && levels && limitsRaw) {
      await propose.mutateAsync({
        kind: "levels",
        key: "levels",
        payload: { ...levels, [chosen]: { ...limitsRaw, min_skills: proposal.minSkills } },
        effective_from,
        note: proposal.note,
      });
    }
  };

  return (
    <McRoot>
      <FrameworkConfigView
        level={chosen}
        access={access}
        framework={framework}
        versions={versions}
        limits={limits}
        loading={list.isLoading || body.isLoading}
        busy={propose.isPending}
        refused={refused}
        failure={failure}
        saved={propose.isSuccess}
        defaultEffective={effective}
        onLevel={(next) => { window.location.hash = `#settings/framework/${next}`; }}
        onSubmit={(proposal) => { void submit(proposal).catch(() => undefined); }}
      />
    </McRoot>
  );
}
